import { FC, useMemo } from 'react';
import { Column, FuzzySearchSelect } from '@/components/UI/FuzzySearchSelect/FuzzySearchSelect';
import { _notNil } from '@/littledash';
import { RiGitForkFill, RiSquareFill } from 'react-icons/ri';
import { PlannedSampleOption, useAssignAnimalsContext } from '@/components/Samples/AssignAnimals/AssignAnimalsProvider';
import { useAddSamplePlanContext } from '@/components/Samples/AddSamplePlanProvider';
import styles from '@/components/Samples/AssignAnimals/AssignAnimalsForm.module.scss';
import { GroupTarget, PlannedAnimalTarget, RegisteredAnimalTarget } from '@/model/SamplePlan.model';
import { aggregateSamplesForAnimal } from '@/components/Samples/AssignAnimals/AssignAnimal.util';

type ItemData = RegisteredAnimalTarget & {
  plannedSamples: Array<PlannedSampleOption>;
  study_group?: GroupTarget;
  animal_definition_api_id?: PlannedAnimalTarget['api_id'];
};

const searchIndex = [
  { name: 'name' },
  { name: ['study_group', 'name'] },
  { name: ['plannedSamples', 'name'] },
  { name: ['plannedSamples', 'sample_name'] },
];

export const RegisteredAnimalSearchSelect: FC = () => {
  const { groups, registered_animals } = useAddSamplePlanContext();
  const {
    selectedRegisteredAnimalTargets,
    updateSelection,
    targetToPlannedSamples,
    plannedSampleOptions,
    registeredToPlannedMap,
  } = useAssignAnimalsContext();

  const items = useMemo<Map<RegisteredAnimalTarget['api_id'], ItemData>>(() => {
    return new Map(
      Array.from(registered_animals.values())
        .map<[RegisteredAnimalTarget['api_id'], ItemData]>((registeredAnimalTarget) => {
          const animal_definition_api_id = registeredToPlannedMap.get(registeredAnimalTarget.api_id);
          const plannedSamples = aggregateSamplesForAnimal(
            [registeredAnimalTarget.study_group_api_id, registeredAnimalTarget.api_id, animal_definition_api_id],
            targetToPlannedSamples,
            plannedSampleOptions
          );
          return [
            registeredAnimalTarget.api_id,
            {
              ...registeredAnimalTarget,
              plannedSamples,
              study_group: _notNil(registeredAnimalTarget.study_group_api_id)
                ? groups.get(registeredAnimalTarget.study_group_api_id)
                : undefined,
              animal_definition_api_id,
            },
          ];
        })
        .sort((left, right) =>
          (left?.[1]?.name ?? '').localeCompare(right?.[1]?.name ?? '', undefined, { numeric: true })
        )
    );
  }, [registered_animals, registeredToPlannedMap, targetToPlannedSamples, plannedSampleOptions, groups]);

  const columns = useMemo<Array<Column<ItemData>>>(
    () => [
      {
        id: 'name',
        title: 'Animal ID',
        cell: (data) => (
          <div className="flex flex-row items-center justify-between w-100">
            <span>{data.name}</span>
            {_notNil(data.animal_definition_api_id) && <RiGitForkFill className="gray" style={{ rotate: '-90deg' }} />}
          </div>
        ),
      },
      {
        id: 'group',
        title: 'Group',
        cell: (data) =>
          _notNil(data.study_group) ? (
            <div className="flex flex-row items-center gap-h-1">
              <RiSquareFill color={data.study_group.color} />
              <span>{data.study_group.name}</span>
            </div>
          ) : (
            '-'
          ),
      },
      {
        id: 'planned_sample_group',
        title: 'Sample definitions',
        cell: (data) => (
          <div className={styles.Chips}>
            {data.plannedSamples.map((sample) => (
              <div className={styles.Chip} key={sample.api_id}>
                {sample.name}
              </div>
            ))}
          </div>
        ),
      },
    ],
    []
  );

  const selected = useMemo(() => {
    const result = new Map<RegisteredAnimalTarget['api_id'], boolean>();
    selectedRegisteredAnimalTargets.forEach((key) => {
      result.set(key, true);
    });
    return result;
  }, [selectedRegisteredAnimalTargets]);

  return (
    <FuzzySearchSelect
      columns={columns}
      items={items}
      searchIndex={searchIndex}
      selected={selected}
      onSelectionChange={(value) => updateSelection({ type: 'target', targetType: 'registered_animals', value })}
    />
  );
};
